import { forwardRef, HTMLAttributes } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Icon } from '@frontend/icons';
import { PopoverMenu, PopoverMenuItem, Text, usePopoverMenu, useTooltip } from '@frontend/design-system';
import { APPT_STATUS_INFO_MAP } from '../../constants';
import { StatusChip, StatusChipProps } from '../status-chip';

export interface EditableStatusChipProps extends StatusChipProps {
  disabled?: boolean;
  disabledTooltipText?: string;
  darkModeMenu?: boolean;
  wrapperStyle?: Interpolation<Theme>;
  onStatusChange?: (status: EventStatus) => void;
}

const APPT_STATUS_MENU_ITEMS = Object.entries(APPT_STATUS_INFO_MAP)
  .filter(([key]) => key !== EventStatus.UNKNOWN_EVENT_STATUS)
  .map(([key, info]) => ({
    value: key as EventStatus,
    ...info,
  }));

export const EditableStatusChip = ({
  disabled,
  disabledTooltipText,
  darkModeMenu,
  wrapperStyle,
  onStatusChange,
  ...restProps
}: EditableStatusChipProps) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({});
  const popoverMenu = usePopoverMenu({ placement: 'bottom-start', darkMode: darkModeMenu });

  if (disabled) {
    if (!disabledTooltipText) {
      return (
        <Wrapper wrapperStyle={wrapperStyle}>
          <StatusChip {...restProps} disabled={disabled} />
        </Wrapper>
      );
    }

    return (
      <>
        <Wrapper wrapperStyle={wrapperStyle}>
          <StatusChip {...restProps} disabled={disabled} {...triggerProps} />
        </Wrapper>
        <Tooltip {...tooltipProps}>{disabledTooltipText}</Tooltip>
      </>
    );
  }

  const popoverMenuTriggerProps = popoverMenu.getTriggerProps({ allowPropagation: false });
  return (
    <>
      <Wrapper wrapperStyle={wrapperStyle} {...popoverMenuTriggerProps}>
        <StatusChip {...restProps} {...popoverMenuTriggerProps} />
      </Wrapper>
      <PopoverMenu {...popoverMenu.getMenuProps()}>
        {APPT_STATUS_MENU_ITEMS.map((item, index) => (
          <PopoverMenuItem
            key={item.value}
            value={item.value}
            {...popoverMenu.getItemProps({ index, onClick: () => onStatusChange?.(item.value) })}
            active={item.value === restProps.status}
          >
            <Icon name={item.icon} />
            <Text as='span'>{item.label}</Text>
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};

export const Wrapper = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { wrapperStyle?: Interpolation<Theme> }
>(({ children, wrapperStyle, ...rest }, ref) => {
  if (!wrapperStyle) return <>{children}</>;

  return (
    <div ref={ref} {...rest} css={wrapperStyle} data-hide-calendar-tooltip='true'>
      {children}
    </div>
  );
});

Wrapper.displayName = 'Wrapper';
